import { useEffect, useMemo, useRef, useState } from 'react'
import uniqBy from 'lodash/uniqBy'

import { Message } from 'lib/_types'
import { useGetChatRoomMessagesQuery } from 'store/api/yay/chat'

type Props = {
  chatId?: number
}

// TODO: change to context?
export const useFetchMessages = ({ chatId }: Props) => {
  const [__aroundMessageId, __aroundMessageIdSet] = useState(0)
  const [__toMessageId, __toMessageIdSet] = useState(0)
  const [__fromMessageId, __fromMessageIdSet] = useState(0)

  const [__messages, __messagesSet] = useState<Message[]>([])
  const messagesRef = useRef(__messages)

  const [__ready, __readySet] = useState(false)
  const [__readyTimestamp, __readyTimestampSet] = useState(0)

  // initial fetch
  const initialMessagesRes = useGetChatRoomMessagesQuery(
    {
      chatId,
      aroundMessageId: __aroundMessageId,
    },
    { skip: !chatId }
  )

  // previous message fetch (top side of screen)
  const messagesPreviousRes = useGetChatRoomMessagesQuery(
    {
      chatId,
      toMessageId: __toMessageId,
    },
    {
      skip: !chatId || !__ready || !__toMessageId,
    }
  )
  const hasMoreTop = Boolean(
    __ready &&
      __messages.length > 0 &&
      (!messagesPreviousRes?.isSuccess ||
        messagesPreviousRes?.data?.messages?.length > 0)
  )

  // next message fetch (bottom side of screen)
  const messagesNextRes = useGetChatRoomMessagesQuery(
    {
      chatId,
      fromMessageId: __fromMessageId,
    },
    {
      skip: !chatId || !__ready || !__fromMessageId,
    }
  )
  const hasMoreBottom = Boolean(
    __ready &&
      __messages.length > 0 &&
      __aroundMessageId && // enable only when in around message mode
      (!messagesNextRes?.isSuccess ||
        messagesNextRes?.data?.messages?.length > 0)
  )

  // keep fetching latest messages every 5s
  // possible cause missing messages if users spam messages more than fetch number within 5s
  // but it is very edge case because fetch number on stg = 15, review/production = 50
  // TODO: better change to trigger or load data by web socket if possible
  const messagesLatestRes = useGetChatRoomMessagesQuery(
    {
      chatId,
    },
    {
      skip:
        !chatId ||
        !__messages?.length ||
        messagesNextRes.isFetching ||
        hasMoreBottom,
      pollingInterval: 5000,
    }
  )

  useEffect(() => {
    if (initialMessagesRes?.isSuccess) {
      __messagesSet(sortAndUpdateParent([...initialMessagesRes.data?.messages]))

      const timeout = setTimeout(() => {
        __readySet(true)
      }, 1000)

      return () => {
        clearTimeout(timeout)
      }
    } else {
      __messagesSet([])
      __readySet(false)
    }
  }, [initialMessagesRes])

  useEffect(() => {
    __messagesSet((prevState) => {
      // `uniqBy` here also cleans up `_sending` messages.
      const uniqMessage = uniqBy(
        [
          ...(messagesLatestRes?.data?.messages ?? []),
          ...(messagesPreviousRes?.data?.messages ?? []),
          ...(messagesNextRes?.data?.messages ?? []),
          ...prevState,
        ],
        (x) => x?.id
      )

      return sortAndUpdateParent(uniqMessage)
    })
  }, [messagesPreviousRes, messagesNextRes, messagesLatestRes])

  useEffect(() => {
    messagesRef.current = __messages

    if (
      __messages?.length &&
      __readyTimestamp !== initialMessagesRes.fulfilledTimeStamp
    ) {
      __readyTimestampSet(initialMessagesRes.fulfilledTimeStamp)
    }
  }, [__messages])

  const isExchanged = useMemo(() => {
    const userList = new Set(__messages.map((m) => m.user_id))
    return userList.size > 1
  }, [__messages])

  return {
    __messages,
    __messagesSet,
    messagesRef,
    isFetching: initialMessagesRes.isFetching,
    __readyTimestamp,

    __toMessageId,
    __toMessageIdSet,
    isFetchingTop: messagesPreviousRes?.isFetching,
    hasMoreTop,

    __fromMessageIdSet,
    isFetchingBottom: messagesNextRes?.isFetching,
    hasMoreBottom,

    __aroundMessageIdSet,
    isExchanged,
  }
}

export const sortAndUpdateParent = (messages: Message[] = []): Message[] =>
  messages
    .sort((a, b) => {
      // manual sort to fix issue https://jira.nanameue.jp/browse/YAY-14057
      if (a?.created_at == b?.created_at) return a?.id - b?.id
      return a?.created_at - b?.created_at
    })
    .map((m, _, arr) => {
      // map mention message to make sure parent data keep updated

      // no mention
      if (!m.parent_id || !m.parent) return m

      const parent = arr.find((a) => a.id === m.parent_id)
      // no parent found
      if (!parent) return m

      return {
        ...m,
        parent: {
          ...parent,
          parent: undefined, // no grand parent here, it will be able to cause infinite nesting issue
        },
      }
    })
